import React, { forwardRef } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

// A controlled value update does not always emit onContentSizeChange on native.
// Let an invisible text copy size the field, including wrapping and Dynamic Type,
// while the same mounted input keeps focus, selection, and native scrolling.
const NativeComposerTextarea = forwardRef(function NativeComposerTextarea({
  value = '', style, maxFontSizeMultiplier, allowFontScaling, ...props
}, ref) {
  const { height, minHeight, maxHeight, ...textStyle } = StyleSheet.flatten(style) || {};
  // The sizing copy only needs enough lines to fill the visible composer.
  // Without a line limit it lays out an arbitrarily tall, invisible document
  // again on every keystroke. Keep the actual input's complete draft intact.
  // One extra line covers fractional line heights and the zero-width sentinel.
  const sizingLineLimit = Number.isFinite(maxHeight) && textStyle.lineHeight > 0
    ? Math.ceil(maxHeight / textStyle.lineHeight) + 1 : undefined;
  return (
    <View style={{ minHeight, maxHeight, overflow: 'hidden' }}>
      <Text
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        pointerEvents="none"
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        allowFontScaling={allowFontScaling}
        numberOfLines={sizingLineLimit}
        ellipsizeMode="clip"
        style={[textStyle, { opacity: 0, flexShrink: 0 }]}
      >
        {/* Even an empty input needs a line of scaled text plus its padding. */}
        {`${value}\u200b`}
      </Text>
      <TextInput
        {...props}
        ref={ref}
        value={value}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        allowFontScaling={allowFontScaling}
        style={[textStyle, StyleSheet.absoluteFillObject]}
      />
    </View>
  );
});

export default NativeComposerTextarea;
